/**
 * Layout of the Management Tools dashboard on the org dashboard's grid model (OE-265): 1-4 columns
 * by container width, each widget spanning whole columns and rows.
 *
 * Saved widgets keep `settings.columns` / `settings.rows` in the old 2/4/6/8 units of the 8-column
 * mat-grid-list, so no stored layout needs rewriting: a span is units / 2.
 */

/** The org dashboard's breakpoints. */
export function gridColumnsForWidth(width: number): number {
    if (width < 500) return 1;
    if (width < 800) return 2;
    if (width < 1200) return 3;
    return 4;
}

/** Saved 2/4/6/8 units as a span of the grid's columns (or rows), at least 1. */
export function spanForUnits(units: number | undefined, available = 4): number {
    const span = Math.ceil((Number(units) || 0) / 2);
    return Math.max(1, Math.min(span, 4, available));
}

/** A span back in the saved units. */
export function unitsForSpan(span: number): number {
    return span * 2;
}

const MIN_ROW = 220;
const MAX_ROW = 380;

/** Square one-by-one cells, as the old 1:1 tiles were, within 220-380px. */
export function rowHeightFor(width: number, columns: number, gap: number): number {
    const cell = (width - gap * (columns - 1)) / columns;
    return Math.round(Math.min(MAX_ROW, Math.max(MIN_ROW, cell)));
}

/** A copy of `items` with the item at `from` moved to `to`. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
    const copy = [...items];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    return copy;
}

/*
 * The fine grid (Paul: "way too big a step"): three fine columns per coarse column (12 across a
 * wide screen) and fixed 40px rows. A width is saved in twelfths (`gridWidth`), a height in rows
 * (`gridHeight`); widgets saved before either existed are read from their old units.
 */
export const FINE_PER_COARSE = 3;
export const ROW_UNIT = 40;
export const LAYOUT_GAP = 12;
export const MIN_ROWS = 2;
export const MAX_ROWS = 40;

export function fineColumnsForWidth(width: number): number {
    return gridColumnsForWidth(width) * FINE_PER_COARSE;
}

/** A saved width in twelfths; old 2/4/6/8 units are quarters of the screen. */
export function widthInTwelfths(settings: { columns?: number; gridWidth?: number } | undefined): number {
    if (settings?.gridWidth) return clamp(Math.round(settings.gridWidth), 1, 12);
    return spanForUnits(settings?.columns) * FINE_PER_COARSE;
}

/** The share of the fine columns there are, never narrower than one coarse column (phones: full width). */
export function colSpanFor(twelfths: number, fineColumns: number): number {
    const min = Math.min(FINE_PER_COARSE, fineColumns);
    return clamp(Math.round((twelfths * fineColumns) / 12), min, fineColumns);
}

/** A saved height in rows; old units become rows of about the same pixels (`legacyCell` = the old square cell). */
export function heightInRows(settings: { rows?: number; gridHeight?: number } | undefined, legacyCell: number): number {
    if (settings?.gridHeight) return clamp(Math.round(settings.gridHeight), MIN_ROWS, MAX_ROWS);
    const span = spanForUnits(settings?.rows);
    const px = span * legacyCell + (span - 1) * LAYOUT_GAP;
    return clamp(Math.round((px + LAYOUT_GAP) / (ROW_UNIT + LAYOUT_GAP)), MIN_ROWS, MAX_ROWS);
}

/** A fine span back in twelfths, for saving. */
export function twelfthsForSpan(span: number, fineColumns: number): number {
    return clamp(Math.round((span * 12) / fineColumns), 1, 12);
}

/** The old 2/4/6/8 units, kept roughly in step for anything that still reads them. */
export function legacyUnits(twelfths: number, rows: number): { columns: number; rows: number } {
    const oldCell = 300 + LAYOUT_GAP;
    return {
        columns: 2 * clamp(Math.round(twelfths / FINE_PER_COARSE), 1, 4),
        rows: 2 * clamp(Math.round((rows * (ROW_UNIT + LAYOUT_GAP)) / oldCell), 1, 4),
    };
}

const WIDTH_NAMES: Record<number, string> = {
    12: 'Full width',
    9: 'Three quarters',
    8: 'Two thirds',
    6: 'Half width',
    4: 'A third',
    3: 'A quarter',
};

export function widthLabel(twelfths: number): string {
    return WIDTH_NAMES[twelfths] || `${twelfths}/12 wide`;
}

/**
 * The pixel height of one row at a grid width (Paul: cards must keep their shape as the window
 * resizes). A card's width is a share of the page, so its rows scale with the page too: 40px at a
 * 1200px grid, more on a wider one, fewer on a narrower one, but never under 28px so content still
 * fits. Phones (one column, every card full width) keep the plain 40px.
 */
export const ROW_REFERENCE_WIDTH = 1200;
export const MIN_ROW_UNIT = 28;
export function rowUnitFor(gridWidth: number): number {
    if (!gridWidth || fineColumnsForWidth(gridWidth) <= FINE_PER_COARSE) return ROW_UNIT;
    return Math.max(MIN_ROW_UNIT, Math.round(((ROW_UNIT * gridWidth) / ROW_REFERENCE_WIDTH) * 10) / 10);
}

/** Pixel height of `rows` fine rows, gaps included. */
export function rowsToPx(rows: number): number {
    return rows * ROW_UNIT + (rows - 1) * LAYOUT_GAP;
}

function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
}

/** The fewest rows that give `px` of height at a row unit, gaps included. */
export function rowsForPx(px: number, rowUnit: number): number {
    if (!(px > 0) || !(rowUnit > 0)) return 0;
    return Math.ceil((px + LAYOUT_GAP) / (rowUnit + LAYOUT_GAP));
}

/**
 * How much taller a section must get to keep its shape once it is stacked across the whole row
 * (fullRowBelow): as many times as it got wider (Paul: the graph stretched flat when it stacked).
 * Phones keep 1: their sections are already full width at their own heights.
 */
export function stackedShapeScale(twelfths: number, fineColumns: number, gridWidth: number, fullRowBelow = 0): number {
    if (!fullRowBelow || !gridWidth || gridWidth >= fullRowBelow || fineColumns <= FINE_PER_COARSE) return 1;
    const natural = colSpanFor(twelfths, fineColumns);
    return natural > 0 && natural < fineColumns ? fineColumns / natural : 1;
}

/**
 * A section's span, never narrower than `minWidth` px: on a narrower screen it takes more columns
 * and its neighbours wrap to the next line, instead of every section squashing (OE-265).
 */
export function colSpanWithMin(
    twelfths: number,
    fineColumns: number,
    gridWidth: number,
    minWidth = 0,
    fullRowBelow = 0
): number {
    const span = colSpanFor(twelfths, fineColumns);
    // Sections that sit side by side stack, each across the whole row, on a page narrower than
    // fullRowBelow px (Paul: the graph still squashed beside the summary when the window narrowed).
    if (gridWidth && gridWidth < fullRowBelow) return fineColumns;
    if (!gridWidth || !minWidth) return span;
    const cell = (gridWidth - LAYOUT_GAP * (fineColumns - 1)) / fineColumns;
    const needed = Math.ceil((minWidth + LAYOUT_GAP) / (cell + LAYOUT_GAP));
    return clamp(Math.max(span, needed), 1, fineColumns);
}
